import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Rocket, Loader2, Edit3, Eye, CheckCircle2, Copy,
  BarChart3, GitBranch, Zap, Globe, Trophy, TrendingUp,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCreditGate } from "@/components/nadia/CreditGate";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  getLanding, generateLanding, patchLandingById, publishLanding,
  getVariants, createVariantB, setTrafficSplit, promoteVariant,
  getLandingStats, getConversionAdapters, setConversionConfig, setDomain,
} from "@/lib/api";

export default function LandingBuilder() {
  const [variants, setVariants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const v = await getVariants();
      setVariants(v);
    } catch {
      const l = await getLanding().catch(() => null);
      setVariants(l ? [l] : []);
    }
  };
  useEffect(() => { load(); }, []);

  const anyLanding = variants[0];

  const { gate, dialog: creditDialog } = useCreditGate();
  const generate = () => gate("ai_landing", async (op_id) => {
    setGenerating(true);
    try {
      await generateLanding(op_id);
      await load();
      toast.success("Nadia ha scritto la landing");
    } catch (e) { if (e?.response?.status !== 402) toast.error("Errore"); throw e; }
    finally { setGenerating(false); }
  });

  const publish = async () => {
    setBusy(true);
    try {
      await publishLanding();
      await load();
      toast.success("Landing pubblicata e collegata alla campagna");
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const publicPath = anyLanding?.slug ? `/l/${anyLanding.slug}` : null;
  const publicUrl = publicPath ? `${window.location.origin}${publicPath}` : null;

  return (
    <>
      {creditDialog}
      <PageHeader
        eyebrow="Landing di Nadia"
        title="Una micro-landing per la tua offerta"
        subtitle="Nadia scrive la pagina, misura il funnel completo Pubblicità → Landing → Conversione e ottimizza in A/B."
        actions={
          <div className="flex gap-2">
            <Button onClick={generate} disabled={generating} variant="outline" className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent" data-testid="regenerate-landing">
              {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
              {anyLanding ? "Rigenera A" : "Genera landing"}
            </Button>
            {anyLanding && (
              <Button onClick={publish} disabled={busy || anyLanding.published} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="publish-landing">
                {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : anyLanding.published ? <CheckCircle2 size={15} className="mr-2" /> : <Rocket size={15} className="mr-2" />}
                {anyLanding.published ? "Pubblicata" : "Pubblica"}
              </Button>
            )}
          </div>
        }
      />

      {!anyLanding && <EmptyState onGenerate={generate} generating={generating} />}

      {anyLanding && (
        <>
          <StatusBar landing={anyLanding} publicPath={publicPath} publicUrl={publicUrl} />

          <Tabs defaultValue="stats" className="mt-2">
            <TabsList className="bg-[#0F172A] border border-white/5 h-auto p-1 flex flex-wrap gap-1" data-testid="landing-tabs">
              <TabsTrigger value="stats" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-200 text-slate-400 gap-1.5"><BarChart3 size={14} /> Statistiche</TabsTrigger>
              <TabsTrigger value="edit" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-200 text-slate-400 gap-1.5"><Edit3 size={14} /> Contenuto</TabsTrigger>
              <TabsTrigger value="ab" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-200 text-slate-400 gap-1.5"><GitBranch size={14} /> A/B Test</TabsTrigger>
              <TabsTrigger value="conv" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-200 text-slate-400 gap-1.5"><Zap size={14} /> Conversione</TabsTrigger>
              <TabsTrigger value="domain" className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-200 text-slate-400 gap-1.5"><Globe size={14} /> Dominio</TabsTrigger>
            </TabsList>

            <TabsContent value="stats" className="mt-4"><StatsTab /></TabsContent>
            <TabsContent value="edit" className="mt-4"><EditTab variants={variants} onReload={load} /></TabsContent>
            <TabsContent value="ab" className="mt-4"><ABTab variants={variants} onReload={load} /></TabsContent>
            <TabsContent value="conv" className="mt-4"><ConversionTab onReload={load} landing={anyLanding} /></TabsContent>
            <TabsContent value="domain" className="mt-4"><DomainTab landing={anyLanding} /></TabsContent>
          </Tabs>
        </>
      )}
    </>
  );
}

function StatusBar({ landing, publicPath, publicUrl }) {
  return (
    <div className="nadia-card p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="landing-status-bar">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">URL pubblico</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-slate-100 truncate">{publicUrl}</span>
          {landing.published ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"><CheckCircle2 size={10} /> Live</span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/10 text-amber-300 border border-amber-500/20">Bozza</span>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(publicUrl).then(() => toast.success("Link copiato"))} className="border-white/10 text-slate-300 hover:text-slate-100 bg-transparent" data-testid="copy-landing-url">
          <Copy size={13} className="mr-1.5" /> Copia
        </Button>
        <a href={publicPath} target="_blank" rel="noreferrer" data-testid="open-landing-preview">
          <Button variant="outline" size="sm" className="border-white/10 text-slate-300 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent">
            <Eye size={13} className="mr-1.5" /> Anteprima
          </Button>
        </a>
      </div>
    </div>
  );
}

/* ---------- Stats + Funnel ---------- */
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    getLandingStats().then(setStats).catch(() => setStats(null));
    import("@/lib/api").then((m) => m.getFunnelSummary().then(setFunnel).catch(() => setFunnel(null)));
  }, []);
  if (!stats || !funnel) return <div className="nadia-card p-6 text-sm text-slate-400">Caricamento…</div>;

  const total = stats.totals;
  const max = Math.max(...funnel.funnel.map((f) => f.value), 1);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="stats-kpis">
        <Metric label="Visite" value={total.visits} />
        <Metric label="Click CTA" value={total.cta_clicks} />
        <Metric label="Form inviati" value={total.form_submits} />
        <Metric label="Conversioni" value={total.conversions} accent />
        <Metric label="Tasso di conversione" value={`${total.conversion_rate}%`} />
        <Metric label="Spesa campagna" value={`€${total.spend}`} />
        <Metric label="Costo per conversione" value={total.cost_per_conversion ? `€${total.cost_per_conversion}` : "—"} accent />
        <Metric label="Ricavo stimato" value={`€${funnel.revenue_estimate}`} />
      </div>

      {/* Funnel */}
      <div className="nadia-card p-6" data-testid="funnel-chart">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-cyan-300" />
          <h3 className="font-heading text-base font-semibold text-slate-100">Funnel completo — Pubblicità → Landing → Conversione</h3>
        </div>
        <div className="space-y-2">
          {funnel.funnel.map((f, i) => {
            const w = Math.max(6, (f.value / max) * 100);
            const drop = i > 0 ? ((funnel.funnel[i - 1].value - f.value) / (funnel.funnel[i - 1].value || 1)) * 100 : 0;
            return (
              <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-${i}`}>
                <div className="w-40 shrink-0 text-xs text-slate-400 font-semibold uppercase tracking-wider">{f.stage}</div>
                <div className="flex-1 h-8 rounded-lg bg-white/[0.03] overflow-hidden relative">
                  <div className="h-full rounded-lg" style={{ width: `${w}%`, background: "linear-gradient(90deg, rgba(34,211,238,0.35), rgba(139,92,246,0.35))" }} />
                  <div className="absolute inset-0 flex items-center px-3 justify-between">
                    <span className="text-sm font-mono text-slate-100">{f.value.toLocaleString("it-IT")}</span>
                    {i > 0 && drop > 0 && (
                      <span className="text-[10px] text-rose-300">-{drop.toFixed(0)}%</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className={`nadia-card p-4 ${accent ? "border-cyan-500/20" : ""}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="font-heading text-2xl font-bold text-slate-50 mt-1">{value}</div>
    </div>
  );
}

/* ---------- Edit tab (champion variant) ---------- */
function EditTab({ variants, onReload }) {
  const champ = variants.find((v) => v.is_champion) || variants[0];
  const [state, setState] = useState(champ);
  useEffect(() => setState(champ), [champ]);

  const save = async (patch) => {
    setState({ ...state, ...patch });
    await patchLandingById(champ.id, patch).catch(() => {});
    onReload();
  };

  if (!state) return null;
  return (
    <>
      <div className="nadia-card p-4 mb-3 flex items-start gap-3">
        <NadiaAvatar size="sm" />
        <p className="text-sm text-slate-300 leading-relaxed">
          Stai modificando la <span className="text-cyan-300 font-semibold">variante {state.variant}</span> (principale). L'anteprima e le statistiche si aggiornano in tempo reale.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Row testId="l-title" label="Titolo hero" value={state.hero_title} onChange={(v) => save({ hero_title: v })} />
        <Row testId="l-sub" label="Sottotitolo" value={state.hero_subtitle} onChange={(v) => save({ hero_subtitle: v })} multiline />
        <Row testId="l-offer-title" label="Titolo offerta" value={state.offer_title} onChange={(v) => save({ offer_title: v })} />
        <Row testId="l-offer-desc" label="Descrizione offerta" value={state.offer_description} onChange={(v) => save({ offer_description: v })} multiline />
        <Row testId="l-terms" label="Termini" value={state.offer_terms} onChange={(v) => save({ offer_terms: v })} />
        <Row testId="l-cta" label="Call to action" value={state.cta_label} onChange={(v) => save({ cta_label: v.toUpperCase() })} />
        <Row testId="l-urgency" label="Riga di urgenza" value={state.urgency_line} onChange={(v) => save({ urgency_line: v })} />
      </div>
    </>
  );
}

/* ---------- A/B tab ---------- */
function ABTab({ variants, onReload }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => getLandingStats().then(setStats).catch(() => setStats(null));
  useEffect(() => { load(); }, [variants.length]);

  const create = async () => {
    setBusy(true);
    try { await createVariantB(); await onReload(); load(); toast.success("Variante B pronta"); }
    catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };
  const changeSplit = async (val) => {
    await setTrafficSplit(val); await onReload(); load();
  };
  const promote = async (v) => {
    await promoteVariant(v); await onReload(); load(); toast.success(`Variante ${v} promossa`);
  };

  const b = variants.find((v) => v.variant === "B");
  const a = variants.find((v) => v.variant === "A");
  return (
    <>
      {!b ? (
        <div className="nadia-card p-6" data-testid="ab-empty">
          <div className="flex items-start gap-4">
            <NadiaAvatar size="sm" />
            <div>
              <h3 className="font-heading text-lg font-semibold text-slate-100 mb-1">Testa una seconda versione</h3>
              <p className="text-sm text-slate-400 max-w-xl mb-4">Nadia riscrive la landing con un'angolazione diversa e distribuisce il traffico 50/50. Non promuoverò una vincitrice senza dati sufficienti (min. 100 visite e 10 conversioni per variante).</p>
              <Button onClick={create} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold" data-testid="create-variant-b">
                {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <GitBranch size={15} className="mr-2" />}
                Crea variante B
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="nadia-card p-5 mb-4" data-testid="traffic-split">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Distribuzione traffico</div>
              <span className="font-mono text-slate-100 text-sm">{a?.traffic_split ?? 50}% / {b?.traffic_split ?? 50}%</span>
            </div>
            <input type="range" min="0" max="100" step="5" value={a?.traffic_split ?? 50} onChange={(e) => changeSplit(Number(e.target.value))} className="w-full accent-cyan-400" data-testid="split-slider" />
            <div className="flex justify-between text-[11px] text-slate-500 mt-1"><span>A</span><span>B</span></div>
          </div>

          {stats && (
            <>
              <VerdictBanner verdict={stats.verdict} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {stats.variants.map((v) => (
                  <VariantCard key={v.id} v={v} onPromote={() => promote(v.variant)} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function VerdictBanner({ verdict }) {
  const status = verdict?.status || "insufficient";
  const styles = {
    winner: "bg-emerald-500/10 border-emerald-500/25 text-emerald-200",
    tie: "bg-slate-500/10 border-slate-500/25 text-slate-200",
    insufficient: "bg-amber-500/10 border-amber-500/25 text-amber-200",
    single_variant: "bg-cyan-500/10 border-cyan-500/25 text-cyan-200",
  };
  return (
    <div className={`nadia-card p-4 flex items-start gap-3 border ${styles[status]}`} data-testid="ab-verdict">
      <NadiaAvatar size="sm" />
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold mb-1">Verdetto di Nadia</div>
        <p className="text-sm leading-relaxed">{verdict?.message}</p>
      </div>
    </div>
  );
}

function VariantCard({ v, onPromote }) {
  return (
    <div className="nadia-card p-5" data-testid={`variant-${v.variant}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-heading text-2xl font-black text-slate-50">Variante {v.variant}</span>
          {v.is_champion && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
              <Trophy size={10} /> Principale
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 font-mono">{v.traffic_split}%</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <MiniMetric label="Visite" value={v.visits} />
        <MiniMetric label="Conversioni" value={v.conversions} accent />
        <MiniMetric label="Tasso" value={`${v.conversion_rate}%`} />
        <MiniMetric label="Costo/conv" value={v.cost_per_conversion ? `€${v.cost_per_conversion}` : "—"} />
      </div>
      {!v.is_champion && (
        <Button onClick={onPromote} variant="outline" size="sm" className="w-full border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent" data-testid={`promote-${v.variant}`}>
          <Trophy size={13} className="mr-1.5" /> Promuovi a principale
        </Button>
      )}
    </div>
  );
}

function MiniMetric({ label, value, accent }) {
  return (
    <div className={`p-3 rounded-xl border ${accent ? "border-cyan-500/25 bg-cyan-500/[0.03]" : "border-white/5 bg-white/[0.02]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-heading text-lg font-bold text-slate-100">{value}</div>
    </div>
  );
}

/* ---------- Conversion tab ---------- */
function ConversionTab({ landing, onReload }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getConversionAdapters().then(setData).catch(() => setData(null)); }, []);

  const current = landing.conversion_config?.type || "booking";

  const change = async (type) => {
    setBusy(true);
    try { await setConversionConfig({ type }); await onReload(); toast.success("Tipo di conversione aggiornato"); }
    catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="nadia-card p-6 text-sm text-slate-400">Caricamento…</div>;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="conversion-types">
        {data.types.map((t) => (
          <button key={t.id} onClick={() => change(t.id)} disabled={busy} data-testid={`conv-${t.id}`}
            className={`text-left nadia-card p-4 transition-colors ${current === t.id ? "border-cyan-500/40 bg-cyan-500/[0.04]" : "hover:border-white/20"}`}>
            <div className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider mb-2 ${current === t.id ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/25" : "bg-white/[0.03] text-slate-400 border border-white/10"}`}>
              {current === t.id ? "Selezionato" : "Disponibile"}
            </div>
            <div className="font-heading text-base font-semibold text-slate-100">{t.label}</div>
            <div className="text-[11px] text-slate-500 mt-1">CTA: {t.cta_default}</div>
            <div className="text-[10px] text-slate-500 mt-1 font-mono">Adapter: {t.adapter}</div>
          </button>
        ))}
      </div>

      <div className="mt-6 mb-2 flex items-center gap-2">
        <h3 className="font-heading text-base font-semibold text-slate-100">Adapter disponibili</h3>
      </div>
      <div className="space-y-2" data-testid="adapters-list">
        {data.adapters.map((a) => (
          <div key={a.id} className="nadia-card p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-100">{a.label}</div>
              {a.env_required?.length ? (
                <div className="text-[10px] text-slate-500 font-mono mt-1 truncate">env: {a.env_required.join(", ")}</div>
              ) : (
                <div className="text-[10px] text-slate-500 mt-1">Native · non richiede credenziali</div>
              )}
            </div>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
              a.mode === "live" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                : a.mode === "native" ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/25"
                : "bg-amber-500/10 text-amber-300 border-amber-500/20"
            }`}>{a.mode}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------- Domain tab ---------- */
function DomainTab({ landing }) {
  const [mode, setMode] = useState(landing.domain?.mode || "subdomain");
  const [sub, setSub] = useState(landing.domain?.subdomain || "");
  const [full, setFull] = useState(landing.domain?.full_domain || "");
  const [saved, setSaved] = useState(landing.domain || null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await setDomain({ mode, subdomain: sub, full_domain: full });
      setSaved(res);
      toast.success(mode === "subdomain" ? "Sottodominio attivato" : "Dominio salvato — verifica DNS in corso");
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <button onClick={() => setMode("subdomain")} data-testid="domain-mode-subdomain"
          className={`nadia-card p-5 text-left ${mode === "subdomain" ? "border-cyan-500/40 bg-cyan-500/[0.04]" : ""}`}>
          <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-semibold mb-1">Sottodominio Nadia</div>
          <div className="text-sm text-slate-100 font-heading font-semibold">nome.nadiacloud.it</div>
          <div className="text-xs text-slate-400 mt-1">Attivo subito, nessuna configurazione DNS.</div>
        </button>
        <button onClick={() => setMode("custom")} data-testid="domain-mode-custom"
          className={`nadia-card p-5 text-left ${mode === "custom" ? "border-cyan-500/40 bg-cyan-500/[0.04]" : ""}`}>
          <div className="text-[11px] uppercase tracking-wider text-violet-300 font-semibold mb-1">Dominio del cliente</div>
          <div className="text-sm text-slate-100 font-heading font-semibold">prenota.tuosito.it</div>
          <div className="text-xs text-slate-400 mt-1">La landing appare sul dominio del tuo cliente, richiede due record DNS.</div>
        </button>
      </div>

      <div className="nadia-card p-5">
        {mode === "subdomain" ? (
          <>
            <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Scegli il sottodominio</label>
            <div className="mt-2 flex items-center">
              <Input value={sub} onChange={(e) => setSub(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="nome-attivita" className="rounded-r-none bg-[#0B1120] border-white/10 h-11" data-testid="subdomain-input" />
              <div className="h-11 px-3 rounded-r-xl bg-white/[0.03] border border-white/10 border-l-0 text-slate-400 text-sm flex items-center">.nadiacloud.it</div>
            </div>
          </>
        ) : (
          <>
            <label className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Dominio completo</label>
            <Input value={full} onChange={(e) => setFull(e.target.value.toLowerCase())} placeholder="prenota.tuosito.it" className="mt-2 bg-[#0B1120] border-white/10 h-11" data-testid="fulldomain-input" />
          </>
        )}
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={busy} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold" data-testid="save-domain">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Globe size={15} className="mr-2" />} Salva
          </Button>
        </div>
      </div>

      {saved && (
        <div className="nadia-card p-5 mt-4" data-testid="domain-status">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Stato</div>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-slate-100 text-sm">{saved.public_hostname}</span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${saved.verified ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-amber-500/10 text-amber-300 border-amber-500/20"}`}>
              {saved.verified ? "Verificato" : "DNS in attesa"}
            </span>
          </div>
          {saved.dns_instructions && (
            <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Record DNS da aggiungere</div>
              <div className="space-y-2 text-xs font-mono">
                {saved.dns_instructions.map((r, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 text-slate-200" data-testid={`dns-record-${i}`}>
                    <span className="text-cyan-300">{r.type}</span>
                    <span className="text-slate-400">{r.name}</span>
                    <span className="truncate">{r.value}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-3">Una volta aggiunti, Nadia verificherà i record automaticamente ogni ora.</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ---------- Shared ---------- */
function Row({ label, value, onChange, multiline, testId }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="nadia-card p-4 group" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
        <button onClick={() => setEditing((e) => !e)} className="text-slate-500 hover:text-cyan-300 opacity-0 group-hover:opacity-100 transition-colors">
          <Edit3 size={13} />
        </button>
      </div>
      {editing ? (
        multiline ? (
          <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={() => setEditing(false)} autoFocus rows={3} className="bg-[#0B1120] border-white/10 text-slate-100 text-sm" />
        ) : (
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={() => setEditing(false)} autoFocus className="bg-[#0B1120] border-white/10 text-slate-100 h-10" />
        )
      ) : (
        <button onClick={() => setEditing(true)} className="w-full text-left text-sm text-slate-100 min-h-[24px] hover:text-cyan-200 transition-colors">
          {value || <span className="italic text-slate-500">tocca per aggiungere</span>}
        </button>
      )}
    </div>
  );
}

function EmptyState({ onGenerate, generating }) {
  return (
    <div className="nadia-card p-10 text-center" data-testid="landing-empty">
      <NadiaAvatar size="lg" thinking={generating} className="mx-auto" />
      <h2 className="mt-6 font-heading text-2xl font-bold text-slate-50">Nessuna landing ancora</h2>
      <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">Con un click Nadia scrive una pagina dedicata all'offerta, con il tuo brand e il form di prenotazione integrato.</p>
      <Button onClick={onGenerate} disabled={generating} className="mt-6 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold" data-testid="empty-generate">
        {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Sparkles size={15} className="mr-2" />}
        Genera con Nadia
      </Button>
    </div>
  );
}
